// The reviewer's verdict at the top of a result: who decided (AI or a
// person), the verdict, the summary, why the rule gave it, and the counts.
// "Why" is worked out from the results with the service's own rule (a
// missed critical item fails the video); nothing here is estimated.
import React from 'react';
import CancelRoundedIcon from '@mui/icons-material/CancelRounded';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import HelpRoundedIcon from '@mui/icons-material/HelpRounded';
import PersonOutlineRoundedIcon from '@mui/icons-material/PersonOutlineRounded';
import { TONE, countResults, fmtDateTime } from './qcFormat';
import { asText } from './qcClient';
import { AI, AiMark } from './aiUi';
import { C, VerdictBadge } from './ui';

const STATUS_ICON = { met: CheckCircleRoundedIcon, missed: CancelRoundedIcon, unclear: HelpRoundedIcon };
const STATUS_TONE = { met: 'good', missed: 'bad', unclear: 'warn' };

export function reasonFor(check) {
    const results = Array.isArray(check?.results) ? check.results : [];
    if (check?.verdict === 'FAIL') {
        const items = results.filter((r) => r.critical && r.status === 'missed');
        return items.length ? { title: 'A critical requirement was missed', rule: 'Any missed critical item fails the video.', items } : null;
    }
    if (check?.verdict === 'NEEDS-REVIEW') {
        const items = results.filter((r) => r.status !== 'met');
        if (items.length) return { title: 'Needs a person to check', rule: 'Nothing critical was missed, but these were not confirmed.', items };
        return results.length ? null : { title: 'Nothing could be checked', rule: 'The reviewer returned no requirement results.', items: [] };
    }
    if (check?.verdict === 'PASS' && results.length && results.every((r) => r.status === 'met')) {
        return { title: results.length === 1 ? 'The one requirement was met' : `All ${results.length} requirements met`, rule: null, items: [] };
    }
    return null;
}

function Reason({ check }) {
    const reason = reasonFor(check);
    if (!reason) return null;
    return (
        <div style={{ marginTop: 14, padding: '12px 14px', background: 'rgba(255,255,255,0.72)', border: `1px solid ${AI.washBorder}`, borderRadius: 12 }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: C.th, textTransform: 'uppercase', letterSpacing: 0.5 }}>Why</div>
            <div style={{ fontSize: 13.5, fontWeight: 700, color: C.text, marginTop: 3 }}>{reason.title}</div>
            {reason.rule && <div style={{ fontSize: 12.5, color: C.muted, marginTop: 1 }}>{reason.rule}</div>}
            {reason.items.length > 0 && (
                <ul style={{ listStyle: 'none', margin: '8px 0 0', padding: 0, display: 'flex', flexDirection: 'column', gap: 5 }}>
                    {reason.items.map((r) => {
                        const Icon = STATUS_ICON[r.status] || HelpRoundedIcon;
                        return (
                            <li key={r.item_id} style={{ display: 'flex', alignItems: 'flex-start', gap: 7, fontSize: 13, color: C.body }}>
                                <Icon aria-hidden="true" style={{ fontSize: 16, marginTop: 1, color: TONE[STATUS_TONE[r.status] || 'warn'].color }} />
                                <div>{asText(r.requirement)}</div>
                            </li>
                        );
                    })}
                </ul>
            )}
        </div>
    );
}

const TALLY = [
    { key: 'met', label: 'Met', tone: 'good' },
    { key: 'missed', label: 'Missed', tone: 'bad' },
    { key: 'unclear', label: 'Unclear', tone: 'warn' },
    { key: 'flagged', label: 'Flagged', tone: 'flag' },
];

function Tally({ check }) {
    const counts = { ...countResults(check.results), flagged: (check.outliers || []).length };
    return (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 88px)', gap: 8, alignContent: 'start' }}>
            {TALLY.map((t) => (
                <div key={t.key} style={{ background: '#fff', border: `1px solid ${C.line}`, borderRadius: 12, padding: '9px 12px' }}>
                    <div style={{ fontSize: 22, fontWeight: 800, lineHeight: 1, color: counts[t.key] ? TONE[t.tone].ink : C.muted }}>{counts[t.key]}</div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 12, fontWeight: 600, color: C.muted, marginTop: 5 }}>
                        <span style={{ width: 7, height: 7, borderRadius: t.tone === 'flag' ? 1 : 7, transform: t.tone === 'flag' ? 'rotate(45deg)' : undefined, background: TONE[t.tone].color }} />
                        {t.label}
                    </div>
                </div>
            ))}
        </div>
    );
}

export default function VerdictCard({ check }) {
    const human = check.kind === 'human';
    const summary = asText(check.summary);
    const note = asText(check.decision_note);
    return (
        <div role="group" aria-label="Verdict" style={{ display: 'flex', gap: 20, flexWrap: 'wrap', padding: 20, borderRadius: 16, background: AI.wash, border: `1px solid ${AI.washBorder}` }}>
            <div style={{ flex: 1, minWidth: 300 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    {human ? (
                        <span aria-hidden="true" style={{ width: 34, height: 34, borderRadius: 10, background: '#fff', border: `1px solid ${C.border}`, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
                            <PersonOutlineRoundedIcon style={{ fontSize: 20, color: C.navy }} />
                        </span>
                    ) : <AiMark size={34} />}
                    <div>
                        <div style={{ fontSize: 12, fontWeight: 700, letterSpacing: 0.5, textTransform: 'uppercase', color: human ? C.navy700 : AI.ink }}>
                            {human ? 'Human decision' : "AI reviewer's verdict"}
                        </div>
                        <div style={{ fontSize: 12, color: C.muted, marginTop: 1 }}>
                            {human ? `By ${asText(check.decided_by, 'a team member')}` : 'Checked against the brief'} · {fmtDateTime(check.created_at)}
                        </div>
                    </div>
                </div>
                <div style={{ marginTop: 14 }}><VerdictBadge verdict={check.verdict} large /></div>
                {summary && <p style={{ margin: '12px 0 0', fontSize: 15, lineHeight: 1.55, color: C.text, maxWidth: 620 }}>{summary}</p>}
                {human && note && <p style={{ margin: '8px 0 0', fontSize: 13, color: C.body }}><b>Reason:</b> “{note}”</p>}
                {!human && <Reason check={check} />}
            </div>
            <Tally check={check} />
        </div>
    );
}
